/**
 * Where securities are held — the on-screen list (Model Portfolios M1, M1-SHOW).
 *
 * Pedro 2026-10-26 (smoke step 6): "please change download the list to Show the list (on screen)
 * and only from there the option to download it." One row per (security, bucket or portfolio),
 * as in the Excel; the download (audited, `securities.manage`) is offered under the list only.
 * Used by the Securities un-approve dialog and the upload preview.
 */
import { useState } from 'react';
import { Button, Group, Loader, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconList } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { securityUsageApi, type SecurityUsage } from '../api/models';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

export function WhereHeldList({ items }: { items: SecurityUsage[] }) {
  const rows = items.flatMap((u) => [
    ...u.buckets.map((b) => ({ u, where: 'Bucket', name: b.name, date: '' })),
    ...(u.models ?? []).map((m) => ({ u, where: 'Model', name: m.name, date: '' })),   // M-SEC: held directly
    ...u.portfolios.map((p) => ({ u, where: 'Portfolio', name: p.label, date: p.as_of })),
  ]);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await securityUsageApi.download(items.map((u) => u.security_id)); }
    catch (e) { notifications.show({ color: 'red', title: 'Download failed', message: errMsg(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Stack gap="xs">
      <ScrollArea.Autosize mah={320}>
        <Table withTableBorder striped verticalSpacing={3}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Security</Table.Th><Table.Th>ISIN</Table.Th><Table.Th>Ccy</Table.Th>
              <Table.Th>Held in</Table.Th><Table.Th>Bucket / model / portfolio</Table.Th><Table.Th>Positions date</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r, i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="xs">{r.u.name}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{r.u.isin}</Text></Table.Td>
                <Table.Td><Text size="xs">{r.u.currency}</Text></Table.Td>
                <Table.Td><Text size="xs">{r.where}</Text></Table.Td>
                <Table.Td><Text size="xs">{r.name}</Text></Table.Td>
                <Table.Td><Text size="xs">{r.date}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      <Group>
        <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} loading={busy} onClick={() => void download()}>
          Download (Excel)
        </Button>
      </Group>
    </Stack>
  );
}

/** "Show the list" for a set of security ids (the upload preview): fetches on click. */
export function ShowWhereHeld({ ids }: { ids: string[] }) {
  const [shown, setShown] = useState(false);
  const q = useQuery({ queryKey: ['securities', 'usage', ...ids], queryFn: () => securityUsageApi.of(ids), enabled: shown });
  if (!shown) {
    return (
      <Group><Button size="xs" variant="light" leftSection={<IconList size={14} />} onClick={() => setShown(true)}>
        Show the list</Button></Group>
    );
  }
  if (q.isLoading) return <Loader size="xs" />;
  if (q.isError) return <Text size="xs" c="red">The list could not be loaded: {errMsg(q.error)}</Text>;
  return <WhereHeldList items={q.data?.items ?? []} />;
}
